import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";

export interface AddressResult {
  address_line1: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  lat: number;
  lng: number;
  formatted_address: string;
}

export interface AddressLookupInput {
  line1: string;
  city: string;
  state: string;
  postal_code: string;
  country?: string;
}

export interface AddressAutocompleteControllerOptions {
  onAddressSelect: (address: AddressResult) => void;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  label?: string;
  apiKey?: string;
  disabled?: boolean;
  id?: string;
}

export interface AddressAutocompleteProps
  extends AddressAutocompleteControllerOptions {
  render: (controller: AddressAutocompleteController) => ReactNode;
}

export interface AddressSuggestion {
  place_id: string;
  description: string;
  main_text: string;
  secondary_text: string;
}

export interface AddressAutocompleteController {
  inputRef: RefObject<HTMLInputElement | null>;
  inputId: string | undefined;
  listboxId: string;
  value: string;
  setValue: (value: string) => void;
  handleInputFocus: () => void;
  handleInputBlur: () => void;
  handleInputKeyDown: (event: KeyboardEvent<Element>) => Promise<void>;
  handleSuggestionSelect: (suggestion: AddressSuggestion) => Promise<void>;
  handleSuggestionHover: (index: number) => void;
  suggestions: AddressSuggestion[];
  suggestionsLoading: boolean;
  showSuggestionPanel: boolean;
  activeIndex: number;
  isLoaded: boolean;
  isAvailable: boolean;
  isDisabled: boolean;
}

interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text?: string;
    secondary_text?: string;
  };
}

interface AutocompleteServiceLike {
  getPlacePredictions: (
    request: {
      input: string;
      componentRestrictions?: { country: string };
    },
    callback: (predictions: PlacePrediction[] | null, status: string) => void,
  ) => void;
}

let googleMapsLoaded = false;
let googleMapsLoadPromise: Promise<void> | null = null;

function hasPlacesLibrary(): boolean {
  return Boolean(
    typeof window !== "undefined" &&
      (
        window as unknown as {
          google?: {
            maps?: {
              places?: {
                AutocompleteService?: unknown;
                PlacesService?: unknown;
              };
            };
          };
        }
      ).google?.maps?.places?.AutocompleteService &&
      (
        window as unknown as {
          google?: {
            maps?: {
              places?: {
                AutocompleteService?: unknown;
                PlacesService?: unknown;
              };
            };
          };
        }
      ).google?.maps?.places?.PlacesService,
  );
}

async function waitForPlacesLibrary(timeoutMs = 5000): Promise<void> {
  if (hasPlacesLibrary()) {
    return;
  }

  const startedAt = Date.now();

  await new Promise<void>((resolve, reject) => {
    const poll = () => {
      if (hasPlacesLibrary()) {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error("Google Places library failed to load"));
        return;
      }

      window.setTimeout(poll, 50);
    };

    poll();
  });
}

async function ensurePlacesLibraryLoaded(): Promise<void> {
  if (hasPlacesLibrary()) {
    return;
  }

  const googleMaps = (
    window as unknown as {
      google?: {
        maps?: {
          importLibrary?: (libraryName: string) => Promise<unknown>;
        };
      };
    }
  ).google?.maps;

  if (googleMaps?.importLibrary) {
    await googleMaps.importLibrary("places");
  }

  await waitForPlacesLibrary();
}

export function getGoogleMapsApiKey(
  explicitApiKey?: string,
): string | undefined {
  return (
    explicitApiKey ??
    (typeof window !== "undefined"
      ? ((window as unknown as Record<string, unknown>).__GOOGLE_MAPS_KEY as
          | string
          | undefined)
      : undefined) ??
    import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  );
}

export function loadGoogleMaps(apiKey: string): Promise<void> {
  if (hasPlacesLibrary()) {
    googleMapsLoaded = true;
    return Promise.resolve();
  }
  if (googleMapsLoaded && hasPlacesLibrary()) return Promise.resolve();
  if (googleMapsLoadPromise) return googleMapsLoadPromise;

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    if (
      typeof window !== "undefined" &&
      (window as unknown as { google?: { maps?: unknown } }).google?.maps
    ) {
      void ensurePlacesLibraryLoaded()
        .then(() => {
          googleMapsLoaded = true;
          resolve();
        })
        .catch((error) => {
          googleMapsLoadPromise = null;
          reject(error);
        });
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      void ensurePlacesLibraryLoaded()
        .then(() => {
          googleMapsLoaded = true;
          resolve();
        })
        .catch((error) => {
          googleMapsLoadPromise = null;
          reject(error);
        });
    };
    script.onerror = () => {
      googleMapsLoadPromise = null;
      reject(new Error("Failed to load Google Maps"));
    };
    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
}

function extractAddressComponent(
  components: google.maps.GeocoderAddressComponent[],
  type: string,
): string {
  const comp = components.find((c) => c.types.includes(type));
  return comp?.long_name ?? "";
}

function extractShortComponent(
  components: google.maps.GeocoderAddressComponent[],
  type: string,
): string {
  const comp = components.find((c) => c.types.includes(type));
  return comp?.short_name ?? "";
}

function formatAddressResult(
  components: google.maps.GeocoderAddressComponent[],
  geometry: google.maps.PlaceGeometry | undefined,
  formattedAddress: string,
): AddressResult {
  const streetNumber = extractAddressComponent(components, "street_number");
  const route = extractAddressComponent(components, "route");
  const premise = extractAddressComponent(components, "premise");
  const subpremise = extractAddressComponent(components, "subpremise");
  const addressLine1 =
    [streetNumber, route].filter(Boolean).join(" ") ||
    [premise, route].filter(Boolean).join(" ") ||
    route ||
    premise ||
    subpremise;

  return {
    address_line1: addressLine1,
    city:
      extractAddressComponent(components, "locality") ||
      extractAddressComponent(components, "postal_town") ||
      extractAddressComponent(components, "sublocality_level_1"),
    state: extractShortComponent(components, "administrative_area_level_1"),
    postal_code: extractAddressComponent(components, "postal_code"),
    country: extractShortComponent(components, "country"),
    lat: geometry?.location.lat() ?? 0,
    lng: geometry?.location.lng() ?? 0,
    formatted_address: formattedAddress,
  };
}

function validateAddressResult(result: AddressResult): AddressResult {
  if (
    !result.address_line1 ||
    !result.city ||
    !result.state ||
    !result.postal_code ||
    Number.isNaN(result.lat) ||
    Number.isNaN(result.lng)
  ) {
    throw new Error("Address is missing required fields");
  }

  return result;
}

function createPlacesService(): google.maps.places.PlacesService {
  return new google.maps.places.PlacesService(document.createElement("div"));
}

function mapPrediction(prediction: PlacePrediction): AddressSuggestion {
  return {
    place_id: prediction.place_id,
    description: prediction.description,
    main_text:
      prediction.structured_formatting?.main_text ?? prediction.description,
    secondary_text: prediction.structured_formatting?.secondary_text ?? "",
  };
}

async function fetchAddressSuggestions(
  input: string,
  apiKey?: string,
): Promise<AddressSuggestion[]> {
  const key = getGoogleMapsApiKey(apiKey);
  if (!key) {
    return [];
  }

  await loadGoogleMaps(key);

  return new Promise<AddressSuggestion[]>((resolve, reject) => {
    const service = new (
      google.maps.places as unknown as {
        AutocompleteService: new () => AutocompleteServiceLike;
      }
    ).AutocompleteService();
    service.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: "us" },
      },
      (predictions: PlacePrediction[] | null, status: string) => {
        if (status === "ZERO_RESULTS") {
          resolve([]);
          return;
        }

        if (status !== "OK" || !predictions) {
          reject(new Error("Address suggestions unavailable"));
          return;
        }

        resolve(predictions.map(mapPrediction));
      },
    );
  });
}

async function fetchPlaceDetails(
  placeId: string,
  fallbackAddress: string,
): Promise<AddressResult> {
  return new Promise<AddressResult>((resolve, reject) => {
    createPlacesService().getDetails(
      {
        placeId,
        fields: ["address_components", "formatted_address", "geometry", "name"],
      },
      (place, detailStatus) => {
        if (detailStatus !== "OK" || !place) {
          reject(new Error("Address could not be verified"));
          return;
        }

        try {
          resolve(
            validateAddressResult(
              formatAddressResult(
                place.address_components ?? [],
                place.geometry,
                place.formatted_address ?? place.name ?? fallbackAddress,
              ),
            ),
          );
        } catch (error) {
          reject(error);
        }
      },
    );
  });
}

export function buildAddressLookupQuery(input: AddressLookupInput): string {
  return [
    input.line1.trim(),
    input.city.trim(),
    input.state.trim(),
    input.postal_code.trim(),
    (input.country ?? "US").trim() || "US",
  ]
    .filter(Boolean)
    .join(", ");
}

export async function geocodeAddress(
  input: AddressLookupInput | string,
  apiKey?: string,
): Promise<AddressResult> {
  const key = getGoogleMapsApiKey(apiKey);
  if (!key) {
    throw new Error("Google Maps API key not configured");
  }

  await loadGoogleMaps(key);

  const address =
    typeof input === "string" ? input : buildAddressLookupQuery(input);

  return new Promise<AddressResult>((resolve, reject) => {
    const places = createPlacesService();
    const resolvePlaceResult = (
      place: google.maps.PlaceResult | null | undefined,
      fallbackAddress: string,
    ) => {
      if (!place) {
        reject(new Error("Address could not be verified"));
        return;
      }

      try {
        resolve(
          validateAddressResult(
            formatAddressResult(
              place.address_components ?? [],
              place.geometry,
              place.formatted_address ?? place.name ?? fallbackAddress,
            ),
          ),
        );
      } catch (error) {
        reject(error);
      }
    };

    places.findPlaceFromQuery(
      {
        query: address,
        fields: ["place_id", "formatted_address", "geometry", "name"],
      },
      (results, status) => {
        if (status !== "OK" || !results?.length) {
          reject(new Error("Address could not be verified"));
          return;
        }

        const firstResult = results[0];
        const fallbackAddress =
          firstResult.formatted_address ?? firstResult.name ?? address;

        if (firstResult.address_components?.length) {
          resolvePlaceResult(firstResult, fallbackAddress);
          return;
        }

        if (!firstResult.place_id) {
          reject(new Error("Address could not be verified"));
          return;
        }

        fetchPlaceDetails(firstResult.place_id, fallbackAddress)
          .then(resolve)
          .catch(reject);
      },
    );
  });
}

/**
 * One authoritative Google Places controller shared by legacy and native-MUI
 * audience renderers. It owns loading, debouncing, keyboard selection, blur
 * timing, controlled/uncontrolled text, and verified place resolution; callers
 * own presentation only.
 */
export function useAddressAutocompleteController({
  onAddressSelect,
  value: controlledValue,
  onValueChange,
  defaultValue = "",
  label,
  apiKey,
  disabled = false,
  id,
}: AddressAutocompleteControllerOptions): AddressAutocompleteController {
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<number | null>(null);
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const [loaded, setLoaded] = useState(
    () => googleMapsLoaded || hasPlacesLibrary(),
  );
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const value = controlledValue ?? uncontrolledValue;
  const key = getGoogleMapsApiKey(apiKey);

  const setValue = useCallback(
    (nextValue: string) => {
      if (controlledValue === undefined) {
        setUncontrolledValue(nextValue);
      }
      onValueChange?.(nextValue);
    },
    [controlledValue, onValueChange],
  );

  useEffect(() => {
    if (controlledValue === undefined) {
      setUncontrolledValue(defaultValue);
    }
  }, [controlledValue, defaultValue]);

  useEffect(() => {
    if (hasPlacesLibrary()) {
      googleMapsLoaded = true;
      setLoaded(true);
      return;
    }

    if (!key) return;

    let cancelled = false;

    void loadGoogleMaps(key)
      .then(() => {
        if (cancelled) {
          return;
        }
        googleMapsLoaded = true;
        setLoaded(true);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [key]);

  useEffect(() => {
    if (loaded || !key) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (!hasPlacesLibrary()) {
        return;
      }

      googleMapsLoaded = true;
      setLoaded(true);
      window.clearInterval(intervalId);
    }, 100);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loaded, key]);

  const isLoaded = loaded || hasPlacesLibrary();
  const listboxId = useMemo(
    () =>
      `${id ?? label?.toLowerCase().replace(/\s+/g, "-") ?? "address-search"}-results`,
    [id, label],
  );

  const handleSuggestionSelect = useCallback(
    async (suggestion: AddressSuggestion) => {
      const result = await fetchPlaceDetails(
        suggestion.place_id,
        suggestion.description,
      );
      setValue(result.formatted_address);
      setSuggestions([]);
      setSuggestionsOpen(false);
      setActiveIndex(-1);
      setIsFocused(false);
      onAddressSelect(result);
    },
    [onAddressSelect, setValue],
  );

  useEffect(() => {
    if (!isLoaded || !key || !isFocused) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      setSuggestionsLoading(false);
      setActiveIndex(-1);
      return;
    }

    const query = value.trim();
    if (query.length < 3) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      setSuggestionsLoading(false);
      setActiveIndex(-1);
      return;
    }

    let cancelled = false;
    setSuggestionsLoading(true);

    const timeoutId = window.setTimeout(() => {
      void fetchAddressSuggestions(query, key)
        .then((nextSuggestions) => {
          if (cancelled) {
            return;
          }
          setSuggestions(nextSuggestions.slice(0, 6));
          setSuggestionsOpen(true);
          setActiveIndex(nextSuggestions.length ? 0 : -1);
        })
        .catch(() => {
          if (cancelled) {
            return;
          }
          setSuggestions([]);
          setSuggestionsOpen(false);
          setActiveIndex(-1);
        })
        .finally(() => {
          if (!cancelled) {
            setSuggestionsLoading(false);
          }
        });
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [isFocused, isLoaded, key, value]);

  useEffect(
    () => () => {
      if (blurTimeoutRef.current !== null) {
        window.clearTimeout(blurTimeoutRef.current);
      }
    },
    [],
  );

  function handleInputFocus() {
    setIsFocused(true);
    if (suggestions.length > 0) {
      setSuggestionsOpen(true);
    }
  }

  function handleInputBlur() {
    if (blurTimeoutRef.current !== null) {
      window.clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = window.setTimeout(() => {
      setIsFocused(false);
      setSuggestionsOpen(false);
    }, 120);
  }

  async function selectSuggestionAt(index: number) {
    const suggestion = suggestions[index];
    if (!suggestion) {
      return;
    }
    await handleSuggestionSelect(suggestion);
  }

  async function handleInputKeyDown(event: KeyboardEvent<Element>) {
    if (!suggestionsOpen || suggestions.length === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % suggestions.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        current <= 0 ? suggestions.length - 1 : current - 1,
      );
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      await selectSuggestionAt(activeIndex >= 0 ? activeIndex : 0);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setSuggestionsOpen(false);
      setActiveIndex(-1);
    }
  }

  const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
  const showSuggestionPanel =
    suggestionsOpen &&
    (suggestionsLoading || suggestions.length > 0 || value.trim().length >= 3);

  return {
    inputRef,
    inputId,
    listboxId,
    value,
    setValue,
    handleInputFocus,
    handleInputBlur,
    handleInputKeyDown,
    handleSuggestionSelect,
    handleSuggestionHover: setActiveIndex,
    suggestions,
    suggestionsLoading,
    showSuggestionPanel,
    activeIndex,
    isLoaded,
    isAvailable: Boolean(key),
    isDisabled: disabled || !key || (!isLoaded && Boolean(key)),
  };
}

export function AddressAutocomplete({
  render,
  ...options
}: AddressAutocompleteProps) {
  return render(useAddressAutocompleteController(options));
}
